import React, { useState } from 'react';
import type { Page } from '../../../types';
import { useAppContext } from '../../../contexts/AppContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useCrmData } from '../../../contexts/CrmProviders';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { isOrgAdminRole } from '../../../domain/support/guards';
import { cn } from '@/lib/utils';
import { AppIcon, type IconName } from '../../../components/icons/AppIcon';
import {
    LAYOUT_NAV_SECTION_LABEL_CLASS,
    cnLayoutNavIcon,
    cnLayoutNavRow,
} from '../../../components/layout/layoutNavTokens';
import { getSlugFromPage } from '../../../utils/routeUtils';
import { handleSidebarNavClick } from '../../../components/layout/sidebar/navHandlers';
import { SidebarAccountSection } from '../../../components/layout/sidebar/SidebarAccountSection';
import { SidebarLogo } from '../../../components/layout/sidebar/SidebarLogo';

type NavItem = { id: Page; label: string; icon: IconName };

const SUPERADMIN_NAV_ITEMS: NavItem[] = [
    { id: 'superadmin', label: 'Dashboard', icon: 'chart' },
    { id: 'superadmin-organizations', label: 'Empresas', icon: 'building' },
    { id: 'superadmin-partners', label: 'Parceiros', icon: 'link' },
    { id: 'superadmin-finance', label: 'Financeiro', icon: 'credit-card' },
    { id: 'superadmin-whatsapp', label: 'Canais WhatsApp', icon: 'whatsapp' },
    { id: 'superadmin-integrations', label: 'Integrações', icon: 'link' },
    { id: 'superadmin-communications', label: 'Comunicações', icon: 'mail' },
    { id: 'superadmin-tickets', label: 'Suporte', icon: 'headset' },
    { id: 'superadmin-comms-whatsapp', label: 'WhatsApp sistema', icon: 'whatsapp' },
];

function NavLink({
    item,
    isActive,
    setCurrentPage,
    onNavigate,
}: {
    item: NavItem;
    isActive: boolean;
    setCurrentPage: (page: Page) => void;
    onNavigate?: () => void;
}) {
    return (
        <a
            href={`/${getSlugFromPage(item.id)}`}
            onClick={(e) => {
                handleSidebarNavClick(e, setCurrentPage, item.id);
                onNavigate?.();
            }}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
                'group flex min-h-11 w-full items-center gap-s3 rounded-lg px-s3 py-s2 text-sm font-medium touch-manipulation',
                cnLayoutNavRow(isActive),
            )}
        >
            <AppIcon name={item.icon} className={cn('h-4 w-4 shrink-0', cnLayoutNavIcon(isActive))} />
            <span className="min-w-0 truncate">{item.label}</span>
        </a>
    );
}

type SuperadminSidebarProps = {
    open?: boolean;
    onClose?: () => void;
};

export function SuperadminSidebar({ open = false, onClose }: SuperadminSidebarProps) {
    const { currentPage, setCurrentPage } = useAppContext();
    const { clientProfile } = useCrmData();
    const { user, logout } = useAuth();
    const { canViewBilling } = usePermissions();
    const { currentOrganization } = useOrganization();
    const canViewSupport = isOrgAdminRole(currentOrganization?.role);
    const [avatarError, setAvatarError] = useState(false);
    const [avatarLoaded, setAvatarLoaded] = useState(false);

    const displayName = clientProfile?.name || user?.user_metadata?.full_name || 'Usuário';
    const initials = (clientProfile?.name || user?.user_metadata?.full_name || 'U')
        .substring(0, 2)
        .toUpperCase();

    return (
        <aside
            id="superadmin-nav"
            aria-label="Navegação superadmin"
            className={cn(
                'flex w-60 shrink-0 flex-col border-r border-subtle bg-card',
                'pt-s4 md:pt-0',
                open ? 'fixed inset-y-0 left-0 z-50 shadow-lg md:static md:z-auto md:shadow-none' : 'hidden',
                'md:flex',
            )}
        >
            <div className="hidden shrink-0 md:block">
                <SidebarLogo isOpen />
            </div>
            <header className="border-b border-subtle px-s4 py-s3 md:hidden">
                <p className="text-sm font-semibold text-default">Plataforma</p>
            </header>

            <nav
                aria-label="Seções da plataforma"
                className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-s4"
            >
                <h2 className={cn(LAYOUT_NAV_SECTION_LABEL_CLASS, 'mb-s2 hidden px-s2 md:block')}>
                    PLATAFORMA
                </h2>
                <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                    {SUPERADMIN_NAV_ITEMS.map((item) => {
                        const isOrganizationsItem = item.id === 'superadmin-organizations';
                        const isActive = isOrganizationsItem
                            ? currentPage === 'superadmin-organizations' ||
                              currentPage === 'superadmin-organization-detail'
                            : currentPage === item.id;

                        return (
                            <li key={item.id}>
                                <NavLink
                                    item={item}
                                    isActive={isActive}
                                    setCurrentPage={setCurrentPage}
                                    onNavigate={onClose}
                                />
                            </li>
                        );
                    })}
                </ul>
            </nav>

            <footer
                className={cn(
                    'mt-auto shrink-0 border-t border-subtle px-s4 pt-s3',
                    'pb-[max(0.75rem,env(safe-area-inset-bottom))] md:pb-s3',
                )}
            >
                <SidebarAccountSection
                    variant="superadmin"
                    isOpen
                    isBlocked={false}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    logout={logout}
                    canViewBilling={canViewBilling}
                    canViewSupport={canViewSupport}
                    clientProfileName={clientProfile?.name}
                    displayName={displayName}
                    userEmail={user?.email}
                    userFullName={user?.user_metadata?.full_name}
                    initials={initials}
                    avatarUrl={user?.user_metadata?.avatar_url}
                    avatarError={avatarError}
                    setAvatarError={setAvatarError}
                    avatarLoaded={avatarLoaded}
                    setAvatarLoaded={setAvatarLoaded}
                    onNavigate={onClose}
                />
            </footer>
        </aside>
    );
}
